/**
 * RunStatusBadge — the deep-research run's execution status as a pill.
 *
 * Single source of truth for "is this run going or done" — shown in the
 * detail header (replacing the legacy hypothesis awaiting_review state) and on
 * the execution/plan card. Queued/running render as an active (spinner) pill;
 * finished_error as destructive; everything else neutral.
 */

import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { HypothesisRunStatus } from '@/types/hypothesis'
import { cn } from '@/utils/cn'

export function RunStatusBadge({
  status,
  running,
}: {
  status?: HypothesisRunStatus
  /** Force the active appearance (e.g. hypothesis flagged running). */
  running?: boolean
}) {
  const { t } = useTranslation()
  const isRunning = running || status === 'running' || status === 'queued'
  const label = isRunning
    ? t('search.detail.runStatusLabel.running')
    : status
      ? t(`search.detail.runStatusLabel.${status}`)
      : null
  if (!label) return null
  const isError = status === 'finished_error'
  return (
    <span
      className={cn(
        'inline-flex w-fit items-center gap-1.5 rounded-full border px-2 py-0.5 text-3xs font-semibold uppercase tracking-wider',
        isRunning
          ? 'border-primary/40 bg-primary/10 text-primary'
          : isError
            ? 'border-destructive/40 bg-destructive/10 text-destructive'
            : 'border-border bg-muted/40 text-muted-foreground'
      )}
    >
      {isRunning && <Loader2 className="size-2.5 animate-spin" />}
      {label}
    </span>
  )
}
